import type { Photo } from '../model/types.ts';
import type { AiResult, ExportRecord, ProjectDoc } from './project.ts';

/**
 * Gallery records. Types and pure list operations from the original `state/library.ts`
 * (zustand store; file deletion is returned to the caller instead of performed).
 */

export interface ProjectRecord {
  id: string;
  /** User-given name (trimmed, max 60 chars); absent = unnamed. */
  name?: string;
  createdAt: number;
  updatedAt: number;
  photo: Photo;
  doc: ProjectDoc;
  /** Newest first. */
  aiResults: AiResult[];
  /** Newest first. */
  exports: ExportRecord[];
}

export const LIBRARY_KEY = 'better-lights.library.v1';
export const LIBRARY_VERSION = 1;

/** Puts the record at the front (most recently edited first), replacing any record with its id. */
export function upsertRecord(projects: ProjectRecord[], record: ProjectRecord): ProjectRecord[] {
  return [record, ...projects.filter((p) => p.id !== record.id)];
}

/** Replaces the record with the same id in place (keeps its position). */
export function replaceRecord(projects: ProjectRecord[], record: ProjectRecord): ProjectRecord[] {
  return projects.map((p) => (p.id === record.id ? record : p));
}

/** Longest project name; keeps tiles and title bars tidy. */
export const PROJECT_NAME_MAX = 60;

/** `name.trim().slice(0, 60)`; empty → undefined (unnamed). */
export function normalizeProjectName(name: string): string | undefined {
  return name.trim().slice(0, PROJECT_NAME_MAX) || undefined;
}

export function renameRecord(projects: ProjectRecord[], id: string, name: string): ProjectRecord[] {
  const clean = normalizeProjectName(name);
  return projects.map((p) => (p.id === id ? { ...p, name: clean } : p));
}

/** Removes a record; returns the URIs the caller must delete (photo, renders, exports). */
export function removeRecord(projects: ProjectRecord[], id: string): { projects: ProjectRecord[]; deleteUris: string[] } {
  const record = projects.find((p) => p.id === id);
  const deleteUris = record ? [record.photo.uri, ...record.aiResults.map((r) => r.uri), ...record.exports.map((e) => e.uri)] : [];
  return { projects: projects.filter((p) => p.id !== id), deleteUris };
}

/** Gallery cover: newest AI render, else the photo. */
export function coverUri(record: ProjectRecord): string {
  return record.aiResults[0]?.uri ?? record.photo.uri;
}
